import { useLocalStorage } from "@jugl-web/utils/hooks/useStorage";
import { useCallback, useMemo } from "react";

const TABLE_GRID_PREFERENCES_STORAGE_KEY = "table-grid-preferences";

interface TableGridStoragePreferences {
  [tableId: string]: {
    columnsOrder: string[];
    columnsWidths: Record<string, number>;
  };
}

export const useTableGridStoragePreferences = (tableId: string) => {
  const [tableGridPreferences, setTableGridPreferences] =
    useLocalStorage<TableGridStoragePreferences>(
      TABLE_GRID_PREFERENCES_STORAGE_KEY,
      {}
    );

  const columnsWidths = useMemo(
    () => tableGridPreferences[tableId]?.columnsWidths || {},
    [tableGridPreferences, tableId]
  );

  const columnsOrder = useMemo(
    () => tableGridPreferences[tableId]?.columnsOrder || [],
    [tableGridPreferences, tableId]
  );

  const updateColumnWidth = useCallback(
    (columnId: string, width: number) => {
      setTableGridPreferences((previousState) => ({
        ...previousState,
        [tableId]: {
          ...previousState[tableId],
          columnsWidths: { ...columnsWidths, [columnId]: width },
        },
      }));
    },
    [columnsWidths, setTableGridPreferences, tableId]
  );

  const updateColumnsOrder = useCallback(
    (order: string[]) => {
      setTableGridPreferences((previousState) => ({
        ...previousState,
        [tableId]: { ...previousState[tableId], columnsOrder: order },
      }));
    },
    [setTableGridPreferences, tableId]
  );

  return {
    columnsWidths,
    columnsOrder,
    updateColumnWidth,
    updateColumnsOrder,
  };
};
